// archive.ts — the device-mode video archive (ARCHITECTURE.md §15.6). Device
// mode tracks on the phone, but the MediaRecorder that runs anyway for a
// possible switch (recorder.ts) is tee'd here, and every chunk is uploaded to
// the store-only archive routes so the scan keeps video/capture.<ext> for
// later re-processing. Nothing on the server re-tracks it.
//
//  * Keyframes first: no chunk goes up while the session has a keyframe
//    upload pending (the injected `paused` predicate, also after Finish:
//    /finish waits for those keyframes) and at most one is in flight while
//    capturing — tracking holds at MAX_PENDING_UPLOADS keyframes, so the
//    video must never starve them. After finish() the rest goes 3 at a time
//    and the capture's /finish does not wait for it.
//  * Unsent chunks stay in memory under ARCHIVE_CAP_BYTES; past it the
//    recording's tail is cut (that chunk and every later one are dropped)
//    and the finish ends at the last kept seq with video_truncated, so the
//    stored file is a playable prefix. Dropping the oldest instead would
//    lose chunk 0 — the container header — and leave nothing decodable.
//  * Resumable: chunk PUTs are idempotent by seq and retried with capped
//    backoff (many attempts: a network loss just stalls the queue), and
//    archive/start answers the seqs the server already stored (so a second
//    start, or one after a server restart, skips them).
//  * A module-level registry keeps an archive alive after CapturePage
//    unmounts (SPA navigation to the viewer); a beforeunload prompt guards
//    unsent chunks and becoming visible again re-kicks the queue (iOS
//    suspends hidden tabs). A reload still loses the unsent tail: the scan
//    then stays video.state 'uploading' (no IndexedDB persistence yet).

import type { ImuBatchRequest, VideoArchiveFinishResponse, VideoArchiveStartRequest, VideoArchiveStartResponse, VideoInfo } from '../api';
import type { VideoChunk } from './recorder';
import { IMU_FLUSH_MS, isRetryable, retryDelay, splitBatches } from './upload';
import type { ImuSampleJSON } from './worker';

// ARCHIVE_CAP_BYTES matches recorder.ts's BUFFER_CAP_BYTES (200 MB unsent).
export const ARCHIVE_CAP_BYTES = 200 * 1024 * 1024;
export const ARCHIVE_IN_FLIGHT_CAPTURE = 1;
export const ARCHIVE_IN_FLIGHT_FINISH = 3;
// ARCHIVE_MAX_ATTEMPTS: with the 15 s backoff cap, ~15 min of outage per chunk.
export const ARCHIVE_MAX_ATTEMPTS = 60;

export interface ArchiveApi {
  videoArchiveStart(id: string, body: VideoArchiveStartRequest): Promise<VideoArchiveStartResponse>;
  putVideoArchiveChunk(id: string, seq: number, chunk: Blob, uploadId: string): Promise<void>;
  postVideoArchiveImu(id: string, body: ImuBatchRequest, uploadId: string): Promise<void>;
  videoArchiveFinish(id: string, lastSeq: number, truncated: boolean, uploadId: string): Promise<VideoArchiveFinishResponse>;
}

export type ArchiveState = 'starting' | 'uploading' | 'finishing' | 'complete' | 'partial' | 'failed' | 'closed';

export interface ArchiveStats {
  state: ArchiveState;
  chunksSeen: number;
  chunksUploaded: number;
  bytesUploaded: number;
  queued: number;
  queuedBytes: number;
  inFlight: number;
  retries: number;
  dropped: number;
  failed: string;
}

export interface ArchiveOptions {
  paused?: () => boolean; // true while keyframe uploads are pending
  capBytes?: number;
  maxAttempts?: number;
  sleep?: (ms: number) => Promise<void>;
  rand?: () => number;
  onStats?: (s: ArchiveStats) => void;
  imuFlushMs?: number; // 0 disables the timer (tests flush by hand)
}

const errMsg = (err: unknown): string => (err instanceof Error ? err.message : String(err));

const registry = new Map<string, VideoArchive>();

// activeArchives lists the archives still uploading (any page).
export function activeArchives(): VideoArchive[] {
  return [...registry.values()];
}

// archiveFor returns the running archive of a scan, if any.
export function archiveFor(scanId: string): VideoArchive | undefined {
  return registry.get(scanId);
}

let hooksInstalled = false;
function installPageHooks(): void {
  if (hooksInstalled || typeof window === 'undefined' || typeof document === 'undefined') return;
  hooksInstalled = true;
  window.addEventListener('beforeunload', (ev) => {
    const unsent = activeArchives().reduce((n, a) => n + a.snapshot.queued + a.snapshot.inFlight, 0);
    if (unsent === 0) return;
    console.warn(`[openscan/archive] page unloading with ${unsent} video chunks unsent`);
    ev.preventDefault();
    ev.returnValue = '';
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') activeArchives().forEach((a) => a.kick());
  });
}

export class VideoArchive {
  private pending = new Map<number, VideoChunk>();
  private seen = new Set<number>();
  private inFlight = new Set<number>();
  private queuedBytes = 0;
  private cutAt = -1; // first seq dropped past the cap (-1: none); every later seq is dropped too
  private started = false;
  private finishing = false;
  private closed = false;
  private failure: Error | null = null;
  private beginP: Promise<void> | null = null;
  private idleWaiters: Array<{ resolve: () => void; reject: (e: Error) => void }> = [];
  private imuBuf: ImuSampleJSON[] = [];
  private imuAngle = 0;
  private imuChain: Promise<void> = Promise.resolve();
  private imuTimer: ReturnType<typeof setInterval> | undefined;
  private readonly capBytes: number;
  private readonly maxAttempts: number;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly rand: () => number;
  private stats: ArchiveStats = {
    state: 'starting', chunksSeen: 0, chunksUploaded: 0, bytesUploaded: 0, queued: 0, queuedBytes: 0, inFlight: 0, retries: 0, dropped: 0, failed: '',
  };
  uploadId = '';
  video: VideoInfo | null = null;

  constructor(readonly scanId: string, private api: ArchiveApi, private opts: ArchiveOptions = {}) {
    this.capBytes = opts.capBytes ?? ARCHIVE_CAP_BYTES;
    this.maxAttempts = opts.maxAttempts ?? ARCHIVE_MAX_ATTEMPTS;
    this.sleep = opts.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
    this.rand = opts.rand ?? Math.random;
    registry.get(scanId)?.close();
    registry.set(scanId, this);
    installPageHooks();
  }

  get snapshot(): ArchiveStats {
    return { ...this.stats, queued: this.pending.size - this.inFlight.size, queuedBytes: this.queuedBytes, inFlight: this.inFlight.size };
  }

  get truncated(): boolean {
    return this.stats.dropped > 0;
  }

  private emit(): void {
    this.opts.onStats?.(this.snapshot);
  }

  private setState(s: ArchiveState): void {
    if (this.stats.state === s) return;
    console.info(`[openscan/archive] scan ${this.scanId}: ${this.stats.state} → ${s}`);
    this.stats.state = s;
    if (s === 'complete' || s === 'partial' || s === 'failed' || s === 'closed') {
      if (registry.get(this.scanId) === this) registry.delete(this.scanId);
      clearInterval(this.imuTimer);
    }
    this.emit();
  }

  private async retry<T>(what: string, fn: () => Promise<T>): Promise<T> {
    for (let attempt = 1; ; attempt++) {
      try {
        return await fn();
      } catch (err) {
        if (this.closed) throw err;
        if (!isRetryable(err) || attempt >= this.maxAttempts) {
          console.error(`[openscan/archive] ${what} failed for good after ${attempt} attempt(s): ${errMsg(err)}`);
          throw err;
        }
        const d = retryDelay(attempt, this.rand);
        this.stats.retries++;
        console.warn(`[openscan/archive] ${what} attempt ${attempt} failed (${errMsg(err)}); retrying in ${d} ms`);
        this.emit();
        await this.sleep(d);
      }
    }
  }

  private fail(message: string): void {
    if (this.failure || this.closed) return;
    this.failure = new Error(message);
    this.stats.failed = message;
    this.setState('failed');
    this.settleIdle();
  }

  // begin POSTs archive/start (retried) and starts the upload queue. The
  // server's `received` seqs are skipped; an already complete video ends it.
  begin(meta: VideoArchiveStartRequest): Promise<void> {
    if (this.beginP) return this.beginP;
    console.info(`[openscan/archive] archive/start scan=${this.scanId} mime=${meta.mime} ${meta.width}x${meta.height} recorder_start_ms=${meta.recorder_start_ms.toFixed(1)}`);
    this.beginP = (async () => {
      let res: VideoArchiveStartResponse;
      try {
        res = await this.retry('archive/start', () => this.api.videoArchiveStart(this.scanId, meta));
      } catch (err) {
        this.fail(`video archive could not start: ${errMsg(err)}`);
        throw err;
      }
      if (this.closed) return;
      this.uploadId = res.upload_id ?? '';
      this.video = res.video ?? null;
      const received = res.received ?? [];
      for (const seq of received) this.markSent(seq);
      this.started = true;
      console.info(`[openscan/archive] upload ${this.uploadId || '(none)'} started; ${received.length} chunks already stored, ${this.pending.size} queued`);
      if (this.video?.state === 'complete') {
        this.pending.clear();
        this.queuedBytes = 0;
        this.setState('complete');
        this.settleIdle();
        return;
      }
      this.setState(this.finishing ? 'finishing' : 'uploading');
      const ms = this.opts.imuFlushMs ?? IMU_FLUSH_MS;
      clearInterval(this.imuTimer);
      if (ms > 0) {
        this.imuTimer = setInterval(() => {
          void this.flushImu();
          this.pump(); // also re-checks `paused` between keyframe uploads
        }, ms);
      }
      this.pump();
    })();
    return this.beginP;
  }

  private markSent(seq: number): void {
    this.seen.add(seq);
    const c = this.pending.get(seq);
    if (c && !this.inFlight.has(seq)) {
      this.pending.delete(seq);
      this.queuedBytes -= c.blob.size;
    }
  }

  // addChunk queues one chunk (duplicates by seq are ignored). Past the cap
  // the tail is cut: this chunk and every later one are dropped.
  addChunk(c: VideoChunk): void {
    if (this.closed || this.failure || this.seen.has(c.seq)) return;
    this.seen.add(c.seq);
    this.stats.chunksSeen++;
    if (this.cutAt < 0 && this.queuedBytes + c.blob.size > this.capBytes && this.pending.size > 0) {
      this.cutAt = c.seq;
      console.warn(`[openscan/archive] over ${Math.round(this.capBytes / 1048576)} MB unsent: cutting the video at chunk ${c.seq} (video_truncated)`);
    }
    if (this.cutAt >= 0 && c.seq >= this.cutAt) {
      this.stats.dropped++;
      this.emit();
      return;
    }
    this.pending.set(c.seq, c);
    this.queuedBytes += c.blob.size;
    this.pump();
    this.emit();
  }

  // oldestQueued is the lowest unsent seq not in flight.
  private oldestQueued(): number | undefined {
    let best: number | undefined;
    for (const seq of this.pending.keys()) {
      if (this.inFlight.has(seq)) continue;
      if (best === undefined || seq < best) best = seq;
    }
    return best;
  }

  addImu(samples: readonly ImuSampleJSON[], screenAngle: number): void {
    if (this.closed || this.failure || samples.length === 0) return;
    for (const s of samples) this.imuBuf.push(s);
    this.imuAngle = screenAngle;
  }

  // flushImu POSTs the buffered samples in order (best effort).
  flushImu(): Promise<void> {
    if (!this.started || this.closed || this.failure || this.imuBuf.length === 0) return this.imuChain;
    const batches = splitBatches(this.imuBuf);
    const angle = this.imuAngle;
    this.imuBuf = [];
    this.imuChain = this.imuChain.then(async () => {
      for (const samples of batches) {
        try {
          await this.retry(`archive imu (${samples.length})`, () => this.api.postVideoArchiveImu(this.scanId, { samples, screen_angle: angle }, this.uploadId));
        } catch (err) {
          if (!this.closed) console.warn(`[openscan/archive] dropping ${samples.length} IMU samples: ${errMsg(err)}`);
        }
      }
    });
    return this.imuChain;
  }

  // kick re-runs the queue (a keyframe upload finished, the tab came back).
  kick(): void {
    this.pump();
  }

  private pump(): void {
    if (!this.started || this.closed || this.failure) return;
    if (this.opts.paused?.()) return; // kick() (or the 1 s timer) resumes it
    const limit = this.finishing ? ARCHIVE_IN_FLIGHT_FINISH : ARCHIVE_IN_FLIGHT_CAPTURE;
    while (this.inFlight.size < limit) {
      const seq = this.oldestQueued();
      if (seq === undefined) break;
      this.inFlight.add(seq);
      void this.uploadOne(this.pending.get(seq)!);
    }
    this.settleIdle();
  }

  private async uploadOne(c: VideoChunk): Promise<void> {
    const t0 = performance.now();
    try {
      await this.retry(`archive chunk ${c.seq}`, () => this.api.putVideoArchiveChunk(this.scanId, c.seq, c.blob, this.uploadId));
      this.stats.chunksUploaded++;
      this.stats.bytesUploaded += c.blob.size;
      console.debug(`[openscan/archive] chunk ${c.seq} ${c.blob.size} B in ${Math.round(performance.now() - t0)} ms (${this.pending.size - 1} unsent)`);
    } catch (err) {
      this.fail(`video archive chunk ${c.seq} failed: ${errMsg(err)}`);
    } finally {
      this.inFlight.delete(c.seq);
      if (this.pending.delete(c.seq)) this.queuedBytes -= c.blob.size;
      this.emit();
      this.pump();
      this.settleIdle();
    }
  }

  private settleIdle(): void {
    if (this.failure) {
      this.idleWaiters.splice(0).forEach((w) => w.reject(this.failure!));
      return;
    }
    if (this.started && this.inFlight.size === 0 && this.pending.size === 0) this.idleWaiters.splice(0).forEach((w) => w.resolve());
  }

  private drained(): Promise<void> {
    if (this.failure) return Promise.reject(this.failure);
    return new Promise<void>((resolve, reject) => {
      this.idleWaiters.push({ resolve, reject });
      this.settleIdle();
    });
  }

  // finish uploads the rest at full speed, flushes the IMU and POSTs
  // archive/finish {last_seq} (the last kept seq when the tail was cut).
  // Resolves with the stored video ('complete' — truncated when cut — or
  // 'partial' when chunks were lost); rejects on failure.
  async finish(lastSeq: number): Promise<VideoInfo | null> {
    if (this.closed) throw new Error('video archive closed');
    if (!this.beginP) throw new Error('video archive was never started');
    this.finishing = true;
    if (this.stats.state === 'uploading') this.setState('finishing');
    console.info(`[openscan/archive] finishing scan ${this.scanId}: last_seq=${lastSeq}, ${this.pending.size} unsent, ${this.stats.dropped} dropped`);
    await this.beginP;
    if (this.stats.state === 'complete') return this.video;
    if (this.cutAt >= 0 && lastSeq >= this.cutAt) {
      console.info(`[openscan/archive] video cut at chunk ${this.cutAt}: finishing at last_seq ${this.cutAt - 1} instead of ${lastSeq}`);
      lastSeq = this.cutAt - 1;
    }
    this.pump();
    await this.drained();
    await this.flushImu();
    if (this.closed) throw new Error('video archive closed');
    let res: VideoArchiveFinishResponse;
    try {
      res = await this.retry('archive/finish', () => this.api.videoArchiveFinish(this.scanId, lastSeq, this.truncated, this.uploadId));
    } catch (err) {
      this.fail(`video archive finish failed: ${errMsg(err)}`);
      throw err;
    }
    this.video = res.video ?? null;
    const missing = res.missing ?? [];
    console.info(`[openscan/archive] archive/finish: ${this.video?.state ?? 'no video'}, ${this.stats.chunksUploaded} chunks / ${Math.round(this.stats.bytesUploaded / 1024)} KiB sent${missing.length ? `, ${missing.length} missing (first ${missing[0]})` : ''}`);
    this.setState(this.video?.state === 'complete' ? 'complete' : 'partial');
    return this.video;
  }

  // close abandons the archive (switch to server mode: video/start wipes it).
  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.pending.clear();
    this.queuedBytes = 0;
    this.idleWaiters.splice(0).forEach((w) => w.reject(new Error('video archive closed')));
    this.setState('closed');
  }
}
